import React from "react";
import { FaSync } from "react-icons/fa";
import {
  COMPARISON_MODES,
  formatBreakdownDateRangeLabel,
} from "../utils/rideBreakdownComparison";

const inputClass =
  "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white";

const ReportCompareFilters = ({
  compareEnabled = false,
  onCompareToggle,
  compareMode = "previous_period",
  onCompareModeChange,
  compareStartDate = "",
  compareEndDate = "",
  onCompareStartChange,
  onCompareEndChange,
  onApplyComparison,
  onClearComparison,
  canApplyComparison = false,
  comparisonLoading = false,
  appliedComparison = null,
  hasMainDateRange = false,
  showApplyComparisonButton = false,
  applyFilterHint = "Choose Start Date and End Date, then generate the report to enable comparison.",
}) => {
  const isCustomCompare = compareMode === "custom";

  const appliedComparisonLabel = appliedComparison
    ? formatBreakdownDateRangeLabel(
        appliedComparison.startDate,
        appliedComparison.endDate
      )
    : "";

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-gray-700">Compare</h4>
          <p className="mt-0.5 text-xs text-gray-500">
            Compare report metrics against another date range.
          </p>
          {appliedComparison && (
            <p className="mt-1 text-xs text-gray-500">
              Comparing vs {appliedComparison.label} ({appliedComparisonLabel})
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-sm font-medium text-gray-700">Compare</span>
          <button
            type="button"
            role="switch"
            aria-checked={compareEnabled}
            onClick={() => onCompareToggle?.(!compareEnabled)}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              compareEnabled ? "bg-blue-600" : "bg-gray-300"
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                compareEnabled ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>
        </div>
      </div>

      {compareEnabled && (
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[180px] flex-1 sm:flex-none">
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
              Compare With
            </label>
            <select
              value={compareMode}
              onChange={(e) => onCompareModeChange?.(e.target.value)}
              className={inputClass}
            >
              {COMPARISON_MODES.filter((mode) => mode.value !== "none").map((mode) => (
                <option key={mode.value} value={mode.value}>
                  {mode.label}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-[160px] flex-1 sm:flex-none">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Compare Start Date
            </label>
            <input
              type="date"
              value={compareStartDate}
              onChange={(e) => onCompareStartChange?.(e.target.value)}
              readOnly={!isCustomCompare}
              className={`${inputClass} read-only:bg-gray-50`}
            />
          </div>
          <div className="min-w-[160px] flex-1 sm:flex-none">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Compare End Date
            </label>
            <input
              type="date"
              value={compareEndDate}
              onChange={(e) => onCompareEndChange?.(e.target.value)}
              readOnly={!isCustomCompare}
              min={isCustomCompare ? compareStartDate || undefined : undefined}
              className={`${inputClass} read-only:bg-gray-50`}
            />
          </div>
          <button
            type="button"
            onClick={onApplyComparison}
            disabled={!canApplyComparison || comparisonLoading}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed ${
              showApplyComparisonButton ? "" : "hidden"
            }`}
          >
            <FaSync className={comparisonLoading ? "animate-spin" : ""} />
            Apply Comparison
          </button>
          {appliedComparison && (
            <button
              type="button"
              onClick={onClearComparison}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {compareEnabled && !showApplyComparisonButton && (
        <p className="mt-2 text-xs text-amber-700">{applyFilterHint}</p>
      )}

      {compareEnabled && showApplyComparisonButton && !hasMainDateRange && compareMode !== "custom" && (
        <p className="mt-2 text-xs text-amber-700">
          Set Start Date and End Date to enable comparison.
        </p>
      )}
    </div>
  );
};

export default ReportCompareFilters;
